/**
 * Image Tool with Size Control for Editor.js
 * Extiende @editorjs/image agregando control de tamaño personalizado
 */

import ImageTool from '@editorjs/image';

class ImageWithSize extends ImageTool {
    constructor({ data, config, api, readOnly }) {
        // Asegurar que data tiene la propiedad size antes de pasar al padre
        const dataWithSize = {
            ...data,
            size: data?.size || 'full'
        };
        
        // Llamar al constructor padre
        super({ data: dataWithSize, config, api, readOnly });
        
        console.log('🔧 ImageWithSize inicializado. File URL:', this._data?.file?.url, 'Size:', this._data?.size);
    }

    render() {
        console.log('🎨 Render llamado. Data:', this._data);
        const imageBlock = super.render();
        console.log('🎨 ImageBlock retornado por super.render():', imageBlock);
        
        // Guardar referencia al elemento para poder actualizarlo después
        this._element = imageBlock;
        
        // Aplicar clase de tamaño al contenedor .cdx-block cuando esté en el DOM
        // Usamos setTimeout para esperar a que Editor.js inserte el elemento
        setTimeout(() => {
            if (this._element && this._data.size) {
                const cdxBlock = this._element.closest('.cdx-block');
                if (cdxBlock) {
                    cdxBlock.classList.add(`image-size-${this._data.size}`);
                    console.log(`✓ Clase aplicada: image-size-${this._data.size}`);
                }
            }
        }, 0);
        
        return imageBlock;
    }

    renderSettings() {
        // Obtener settings originales de ImageTool
        const originalSettings = super.renderSettings ? super.renderSettings() : [];
        
        // Debug: Mostrar el valor actual del tamaño
        console.log(`📏 Renderizando settings - Tamaño actual: ${this._data.size}`);
        
        // Crear settings con referencias para poder actualizar después
        const sizes = ['small', 'medium', 'large', 'full'];
        const labels = ['Pequeña (33%)', 'Mediana (50%)', 'Grande (75%)', 'Completa (100%)'];
        const icons = [
            `<svg width="20" height="20" viewBox="0 0 20 20"><text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="currentColor">33%</text></svg>`,
            `<svg width="20" height="20" viewBox="0 0 20 20"><text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="currentColor">50%</text></svg>`,
            `<svg width="20" height="20" viewBox="0 0 20 20"><text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="currentColor">75%</text></svg>`,
            `<svg width="20" height="20" viewBox="0 0 20 20"><rect x="2" y="2" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"/></svg>`
        ];
        
        // Crear settings personalizados
        const sizeSettings = sizes.map((size, index) => ({
            icon: icons[index],
            label: labels[index],
            // NO usar toggle para que sean mutuamente exclusivos
            isActive: this._data.size === size,
            onActivate: () => {
                console.log(`👆 Click en: ${size}`);
                
                // Actualizar el tamaño
                this.setSize(size);
                
                // Forzar re-render del panel de settings cerrándolo y reabriéndolo
                if (this.api && this.api.toolbar) {
                    setTimeout(() => {
                        this.api.toolbar.close();
                        setTimeout(() => {
                            this.api.toolbar.open();
                        }, 50);
                    }, 100);
                }
            }
        }));

        // Combinar settings personalizados con los originales
        return [...sizeSettings, ...originalSettings];
    }

    setSize(newSize) {
        // Actualizar el dato interno
        const oldSize = this._data.size;
        this._data.size = newSize;
        
        // Buscar el contenedor .cdx-block que Editor.js crea automáticamente
        if (this._element) {
            const cdxBlock = this._element.closest('.cdx-block');
            
            if (cdxBlock) {
                // Remover clase de tamaño anterior
                if (oldSize) {
                    cdxBlock.classList.remove(`image-size-${oldSize}`);
                }
                
                // Agregar la nueva clase de tamaño
                cdxBlock.classList.add(`image-size-${newSize}`);
                
                console.log(`✓ Tamaño cambiado a: ${newSize} (${newSize === 'small' ? '33%' : newSize === 'medium' ? '50%' : newSize === 'large' ? '75%' : '100%'})`);
            }
        }
    }

    save(blockContent) {
        const savedData = super.save(blockContent);
        
        return {
            ...savedData,
            size: this._data.size
        };
    }
    
    // Sobrescribir setTune para manejar stretched, withBorder, withBackground sin errores
    setTune(tune, value) {
        // Asegurar que _data existe antes de modificarlo
        if (!this._data) {
            this._data = {
                file: {},
                caption: '',
                withBorder: false,
                withBackground: false,
                stretched: false,
                size: 'full'
            };
        }
        
        // Ahora sí modificar la propiedad
        this._data[tune] = value;
        
        console.log(`🎛️ Tune '${tune}' cambiado a:`, value);
        
        // Aplicar el cambio al UI si existe el elemento
        if (this._element) {
            const cdxBlock = this._element.closest('.cdx-block');
            if (cdxBlock && tune === 'stretched') {
                if (value) {
                    cdxBlock.classList.add('cdx-block--stretched');
                } else {
                    cdxBlock.classList.remove('cdx-block--stretched');
                }
            }
        }
    }
    
    static get toolbox() {
        return {
            title: 'Imagen',
            icon: '<svg width="17" height="15" viewBox="0 0 336 276"><path fill="currentColor" d="M291 150V79c0-19-15-34-34-34H79c-19 0-34 15-34 34v42l67-44 81 72 56-29 42 30zm0 52l-43-30-56 30-81-67-66 39v23c0 19 15 34 34 34h178c17 0 31-13 34-29zM79 0h178c44 0 79 35 79 79v118c0 44-35 79-79 79H79c-44 0-79-35-79-79V79C0 35 35 0 79 0z"/></svg>'
        };
    }
}

export default ImageWithSize;
